"use client";

import { type DefaultImageProps, Image } from "@pamperedchef/design-system";

import { FeaturedText, RichText } from "@/composites/foundation";
import { className } from "@/composites/foundation/utilities/className";
import type { CmsImage } from "@/composites/landing/types";
import * as styles from "./GiftHero.module.scss";

export interface GiftHeroProps {
	headline: string;
	/** HTML, placeholders already filled. */
	copy?: string;
	imageDesktopLeft?: CmsImage;
	imageDesktopRight?: CmsImage;
	imageMobile?: CmsImage;
}

/**
 * Full-bleed hero. Desktop shows a photo either side of the copy; phones show
 * the mobile photo as a band above it.
 */
export function GiftHero({
	headline,
	copy,
	imageDesktopLeft,
	imageDesktopRight,
	imageMobile
}: GiftHeroProps) {
	return (
		<section className={styles["gift-hero"]} data-component="GiftHero">
			{imageMobile ? (
				<div className={styles["gift-hero__mobile"]} aria-hidden="true">
					<Image<DefaultImageProps>
						src={imageMobile.url}
						alt=""
						className={styles["gift-hero__band"]}
						// @ts-expect-error DS types only the ratio key both sets share (1:1); 16:9 exists at runtime.
						aspectRatio="16:9"
						objectFit="cover"
					/>
				</div>
			) : null}

			{imageDesktopLeft ? (
				<div
					{...className(
						styles["gift-hero__side"],
						styles["gift-hero__side--left"]
					)}
					aria-hidden="true"
				>
					<Image
						src={imageDesktopLeft.url}
						alt=""
						className={styles["gift-hero__image"]}
						objectFit="cover"
					/>
				</div>
			) : null}

			<div className={styles["gift-hero__content"]}>
				<FeaturedText
					className={styles["gift-hero__headline"]}
					headline={headline}
					styledAs="display-md"
					alignment="center"
					headingTag="h1"
				/>
				{copy ? (
					<RichText
						content={copy}
						className={styles["gift-hero__copy"]}
					/>
				) : null}
			</div>

			{imageDesktopRight ? (
				<div
					{...className(
						styles["gift-hero__side"],
						styles["gift-hero__side--right"]
					)}
					aria-hidden="true"
				>
					<Image
						src={imageDesktopRight.url}
						alt=""
						className={styles["gift-hero__image"]}
						objectFit="cover"
					/>
				</div>
			) : null}
		</section>
	);
}
